import React, { useRef, useEffect } from 'react';
import { InputBase, Box, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { SearchMode } from './types';

interface CommandPaletteInputProps {
  query: string;
  mode: SearchMode;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  resultCount: number;
  activeIndex: number;
}

const MODE_LABELS: Record<SearchMode, string | null> = {
  general: null,
  actions: 'Actions',
  caseId: 'Case ID',
  contacts: 'Contacts',
  myCases: 'My Cases',
};

const CommandPaletteInput: React.FC<CommandPaletteInputProps> = ({
  query,
  mode,
  onChange,
  onKeyDown,
  resultCount,
  activeIndex,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus when mounted
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, []);

  const modeLabel = MODE_LABELS[mode];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 22 }} />
      {modeLabel && (
        <Chip
          label={modeLabel}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mr: 1, height: 24 }}
        />
      )}
      <InputBase
        inputRef={inputRef}
        value={query}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Search or type > for actions, # for case ID, @ for contacts..."
        fullWidth
        sx={{ fontSize: 15 }}
        inputProps={{
          role: 'combobox',
          'aria-expanded': resultCount > 0,
          'aria-controls': 'command-palette-results',
          'aria-activedescendant':
            resultCount > 0 ? `command-palette-result-${activeIndex}` : undefined,
          'aria-label': 'Search commands and content',
          autoComplete: 'off',
        }}
      />
    </Box>
  );
};

export default CommandPaletteInput;
